import { useEffect, useState } from "react";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { ShoppingCart } from "lucide-react";

const curMonth = () => {
    const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const nextMonth = () => {
    const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function ProcurementPlannerPage() {
    const [month, setMonth] = useState(nextMonth());
    const [consumption, setConsumption] = useState(null);
    const [suggested, setSuggested] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [openPRs, setOpenPRs] = useState(null);
    const [savingCell, setSavingCell] = useState(null);

    async function load() {
        try {
            const cm = curMonth();
            const [c, s, f, p] = await Promise.all([
                api.get("/procurement/consumption", { params: { month: cm } }),
                api.get("/procurement/suggested", { params: { month } }),
                api.get("/procurement/forecast", { params: { month } }),
                api.get("/procurement/open-prs"),
            ]);
            setConsumption(c.data);
            setSuggested(s.data);
            setForecast(f.data);
            setOpenPRs(p.data);
        } catch (e) { toast.error(apiError(e)); }
    }
    useEffect(() => { load(); // eslint-disable-next-line
    }, [month]);

    async function saveCell(site_id, category_id, val) {
        setSavingCell(`${site_id}|${category_id}`);
        try {
            const amount = val === "" || val == null ? null : parseFloat(val);
            await api.put(`/procurement/forecast/${site_id}/${category_id}`, { amount }, { params: { month } });
            toast.success("Saved");
            const { data } = await api.get("/procurement/forecast", { params: { month } });
            setForecast(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSavingCell(null); }
    }

    const suggestedGroups = suggested?.groups ?? [];
    const forecastRows = forecast?.rows ?? [];
    const forecastCols = forecastRows[0]?.cells ?? [];
    const openBuckets = openPRs?.buckets ?? {};
    const openTotal = openPRs?.total ?? 0;
    const consumptionSites = consumption?.sites ?? [];

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500">Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Procurement planner</h1>
                </div>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-gray-200 px-3 py-2 text-sm" data-testid="planner-month" />
            </div>

            <section className="mt-6">
                <div className="text-sm font-bold text-[#1E3355] mb-2 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Suggested combined purchase (next month, grouped by preferred vendor · aggregated across sites)</div>
                <div className="rounded-xl bg-white border border-gray-100 divide-y divide-gray-100">
                    {suggested && suggestedGroups.length === 0 && <div className="p-6 text-gray-400" data-testid="no-suggested">No items need restocking.</div>}
                    {suggestedGroups.map((g) => (
                        <div key={g.vendor_name} className="p-4" data-testid={`sug-group-${g.vendor_name}`}>
                            <div className="flex items-baseline justify-between">
                                <div className="font-semibold text-[#1E3355]">{g.vendor_name}
                                    <span className="text-xs text-gray-500 ml-2">{g.total_line_count ?? (g.items?.length ?? 0)} item{(g.total_line_count ?? 0) === 1 ? "" : "s"} · {g.site_count ?? 0} site{(g.site_count ?? 0) === 1 ? "" : "s"}</span>
                                </div>
                                {(g.indicative_value ?? 0) > 0 && <div className="text-sm font-semibold text-[#1E3355]">₹ {Number(g.indicative_value).toLocaleString("en-IN")}<span className="text-xs text-gray-400 font-normal ml-1">indicative</span></div>}
                            </div>
                            <div className="mt-2 overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="text-xs text-gray-500 uppercase tracking-wider"><tr><th className="text-left px-2 py-1">Item</th><th className="text-left px-2 py-1">Category</th><th className="text-right px-2 py-1">Total qty</th><th className="text-left px-2 py-1">Unit</th><th className="text-right px-2 py-1">Last-paid ₹/unit</th><th className="text-left px-2 py-1">Per-site breakdown</th></tr></thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {(g.items ?? []).map((it) => (
                                            <tr key={it.item_id} data-testid={`sug-item-${it.item_name}`}>
                                                <td className="px-2 py-1 font-medium">{it.item_name}</td>
                                                <td className="px-2 py-1 text-xs">{it.category}</td>
                                                <td className="px-2 py-1 text-right tabular-nums font-semibold">{it.total_qty}</td>
                                                <td className="px-2 py-1 text-xs">{it.unit}</td>
                                                <td className="px-2 py-1 text-right tabular-nums">
                                                    {it.last_paid_unit_rate == null
                                                        ? <span className="text-gray-400">n/a</span>
                                                        : <span title={`from ${it.last_paid_source}${it.last_paid_vendor_name ? " · " + it.last_paid_vendor_name : ""}`}>
                                                            {Number(it.last_paid_unit_rate).toLocaleString("en-IN")}
                                                            <span className={`ml-1 text-[10px] px-1 rounded ${it.last_paid_source === "expense_line" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{it.last_paid_source === "expense_line" ? "expense" : "rate note"}</span>
                                                        </span>}
                                                </td>
                                                <td className="px-2 py-1 text-xs text-gray-600">
                                                    {(it.per_site ?? []).map((ps, i) => (
                                                        <span key={i} className="inline-block mr-2 whitespace-nowrap">
                                                            {ps.site_name}: <span className="tabular-nums font-medium">{ps.suggested_qty}</span>
                                                        </span>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-8">
                <div className="text-sm font-bold text-[#1E3355] mb-2">Budget forecast (3-mo avg by category, HO-editable per cell)</div>
                <div className="rounded-xl bg-white border border-gray-100 overflow-x-auto">
                    {forecast ? (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-gray-500 uppercase tracking-wider bg-[#F4F6F9]">
                                <tr><th className="text-left px-2 py-1.5">Site</th>
                                    {forecastCols.map((c) => <th key={c.category_id} className="text-right px-2 py-1.5">{c.category_name}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {forecastRows.map((r) => (
                                    <tr key={r.site_id}>
                                        <td className="px-2 py-1.5 font-medium text-[#1E3355]">{r.site_name}</td>
                                        {(r.cells ?? []).map((c) => (
                                            <td key={c.category_id} className="px-2 py-1.5 text-right tabular-nums">
                                                <ForecastCell
                                                    siteId={r.site_id} catId={c.category_id}
                                                    value={c.value} avg3={c.avg3}
                                                    override={c.override}
                                                    saving={savingCell === `${r.site_id}|${c.category_id}`}
                                                    onSave={(v) => saveCell(r.site_id, c.category_id, v)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <div className="p-6 text-gray-400">Loading…</div>}
                </div>
            </section>

            <section className="mt-8">
                <div className="text-sm font-bold text-[#1E3355] mb-2">Open reorder suggestions (existing PR queue, grouped by status)</div>
                <div className="rounded-xl bg-white border border-gray-100">
                    {!openPRs ? (
                        <div className="p-6 text-gray-400">Loading…</div>
                    ) : openTotal === 0 ? (
                        <div className="p-6 text-gray-400" data-testid="no-prs">No open purchase requests.</div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {["REQUESTED", "APPROVED", "ORDERED"].map((st) => {
                                const rows = openBuckets[st] ?? [];
                                if (rows.length === 0) return null;
                                return (
                                    <div key={st} className="p-3" data-testid={`prs-${st}`}>
                                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{st} · {rows.length}</div>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead className="text-xs text-gray-500 uppercase tracking-wider"><tr><th className="text-left px-2 py-1">Site</th><th className="text-left px-2 py-1">Item</th><th className="text-right px-2 py-1">Qty suggested</th><th className="text-left px-2 py-1">Raised</th></tr></thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {rows.slice(0, 30).map((p) => (
                                                        <tr key={p.id} data-testid={`pr-${p.id}`}>
                                                            <td className="px-2 py-1">{p.site_name}</td>
                                                            <td className="px-2 py-1">{p.item_name}</td>
                                                            <td className="px-2 py-1 text-right tabular-nums">{p.qty_suggested ?? p.qty_ordered ?? "—"}</td>
                                                            <td className="px-2 py-1 text-xs">{fmtDate(p.created_at)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {rows.length > 30 && <div className="text-xs text-gray-400 mt-1">…and {rows.length - 30} more</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {consumption && (
                <section className="mt-8">
                    <div className="text-sm font-bold text-[#1E3355] mb-2">Consumption &amp; cost this month
                        <span className="text-xs text-gray-500 font-normal ml-2">Item-level from stock movement (where available) · category-level ₹ from expenses</span>
                    </div>
                    {consumptionSites.filter((s) => ((s.items ?? []).length + (s.categories ?? []).length) > 0).map((s) => (
                        <div key={s.site_id} className="rounded-xl bg-white border border-gray-100 mt-2 p-4">
                            <div className="font-semibold text-[#1E3355]">{s.site_name}</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                <div>
                                    <div className="text-xs text-gray-500 mb-1">Item-level qty</div>
                                    {(s.items ?? []).length === 0 ? <div className="text-xs text-gray-400">no stock movement recorded</div> : (
                                        <table className="min-w-full text-xs">
                                            <tbody>{(s.items ?? []).slice(0, 8).map((it) => <tr key={it.item_id}><td className="py-0.5">{it.item_name}<span className="ml-1 inline-block px-1 rounded bg-blue-50 text-blue-700 text-[10px]">item</span></td><td className="py-0.5 text-right tabular-nums">{it.qty}</td></tr>)}</tbody>
                                        </table>
                                    )}
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1">Category-level ₹</div>
                                    {(s.categories ?? []).length === 0 ? <div className="text-xs text-gray-400">no expenses this month</div> : (
                                        <table className="min-w-full text-xs">
                                            <tbody>{(s.categories ?? []).map((c) => <tr key={c.category}><td className="py-0.5">{c.category}<span className="ml-1 inline-block px-1 rounded bg-purple-50 text-purple-700 text-[10px]">category</span></td><td className="py-0.5 text-right tabular-nums">₹ {Number(c.cost).toLocaleString("en-IN")}</td></tr>)}</tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </section>
            )}
        </div>
    );
}

function ForecastCell({ siteId, catId, value, avg3, override, saving, onSave }) {
    const [local, setLocal] = useState(value);
    useEffect(() => setLocal(value), [value]);
    return (
        <div className="flex items-center gap-1 justify-end">
            <input type="number" min="0" step="1" value={local ?? ""} onChange={(e) => setLocal(e.target.value)}
                onBlur={() => { if ((local ?? "") !== (value ?? "")) onSave(local); }}
                className={`w-24 rounded border px-1.5 py-1 text-right text-sm ${override != null ? "border-emerald-400 bg-emerald-50" : "border-gray-200"}`}
                data-testid={`fc-${siteId}-${catId}`}
                disabled={saving}
            />
            {override != null && <span className="text-[10px] text-emerald-700" title={`avg was ₹${avg3}`}>ov</span>}
        </div>
    );
}
